/**
 * FlagUnitCard — S6 field staff flagging. Staff flag a unit whose residents
 * appear to have changed; the unit auto-holds and the NECS Account Manager
 * side is notified to chase the leasing manager.
 */
import { useEffect, useState } from 'react';
import { Flag } from 'lucide-react';
import { apiFetch } from '../lib/apiFetch';
import { toast } from 'sonner';

const GOLD = '#C9A962';

export default function FlagUnitCard() {
  const [props, setProps] = useState([]);
  const [f, setF] = useState({ property_id: '', unit_number: '', reason: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const rows = await apiFetch('/api/properties');
        setProps(Array.isArray(rows) ? rows : rows.properties || []);
      } catch (_) { setProps([]); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiFetch('/api/resident/flag-unit', { method: 'POST', body: f });
      toast.success(`Unit flagged — ${res.accounts_held} account(s) placed on hold, Account Manager notified`);
      setF({ property_id: '', unit_number: '', reason: '' });
    } catch (err) { toast.error(err.detail || 'Flag failed'); }
    finally { setBusy(false); }
  };

  const inputCls = 'rounded bg-zinc-800 border border-zinc-600 px-2 py-2 text-white text-xs w-full';

  return (
    <div className="rounded-xl border border-zinc-700 bg-zinc-900/70 p-4" data-testid="flag-unit-card">
      <h3 className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>
        <Flag className="h-4 w-4" /> Flag Unit Occupancy Change
      </h3>
      <p className="text-[10px] text-zinc-500 mb-3">Residents look different or account info seems outdated? Flag the unit — it auto-holds and the Account Manager contacts leasing.</p>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-4 gap-2">
        <select required value={f.property_id} onChange={(e) => setF((s) => ({ ...s, property_id: e.target.value }))}
          className={inputCls} data-testid="fu-property-select">
          <option value="">Property…</option>
          {props.map((p) => <option key={p.property_id} value={p.property_id}>{p.property_name}</option>)}
        </select>
        <input required placeholder="Unit #" value={f.unit_number}
          onChange={(e) => setF((s) => ({ ...s, unit_number: e.target.value }))}
          className={inputCls} data-testid="fu-unit-input" />
        <input required minLength={5} placeholder="What did you notice?" value={f.reason}
          onChange={(e) => setF((s) => ({ ...s, reason: e.target.value }))}
          className={`${inputCls} sm:col-span-2`} data-testid="fu-reason-input" />
        <button type="submit" disabled={busy}
          className="sm:col-span-4 sm:w-fit px-4 py-2 rounded-full text-xs font-bold disabled:opacity-50"
          style={{ backgroundColor: GOLD, color: '#1a1a1a' }} data-testid="fu-submit-btn">
          {busy ? 'Flagging…' : 'Flag Unit'}
        </button>
      </form>
    </div>
  );
}
